const onBankTransactionsPageMatch = () => {
  const canSeeOpenBalances =
    isInRole('bank_transaction_balances') ||
    isInRole('tech') ||
    isInRole('hv_tesche_buha')
  if (!canSeeOpenBalances) {
    return
  }

  // Cache for unit and property responses
  const unitCache = new Map()
  const propertyCache = new Map()

  // Function to format number with euro symbol
  function formatNumber(number) {
    if (number === null || number === undefined || isNaN(number)) {
      return 'N/A'
    }
    // Format with thousands separator (.) and decimal comma (,)
    return `${number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace('.', ',')} €`
  }

  // Function to extract unit ID from data-row-key
  function extractUnitId(rowKey) {
    const parts = rowKey.split('-')
    return parts[parts.length - 1]
  }

  // Function to fetch unit details
  async function fetchUnitDetails(unitId) {
    // Check cache first
    if (unitCache.has(unitId)) {
      return unitCache.get(unitId)
    }

    try {
      const token = JSON.parse(localStorage.auth).token
      const response = await fetch(`/services/pmp-accounting/api/v1/units/get-by-id/${unitId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await response.json()
      // Cache the propertyId
      unitCache.set(unitId, data.propertyId)
      return data.propertyId
    } catch (error) {
      console.error('Error fetching unit details:', error)
      return null
    }
  }

  // Function to fetch property HR ID
  async function fetchPropertyHrId(propertyId) {
    // Check cache first
    if (propertyCache.has(propertyId)) {
      return propertyCache.get(propertyId)
    }

    try {
      const token = JSON.parse(localStorage.auth).token
      const response = await fetch(`/services/pmp-accounting/api/v1/properties?propertyId=${propertyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await response.json()
      // Cache the propertyHrId
      propertyCache.set(propertyId, data.propertyHrId)
      return data.propertyHrId
    } catch (error) {
      console.error('Error fetching property HR ID:', error)
      return null
    }
  }

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  /**
   * One alternation for all account codes of a property. Longer codes first so
   * alternation is unambiguous for prefix relationships (e.g. 12 vs 123).
   */
  function buildAccountCodeRegex(accountCodes) {
    const unique = [...new Set(accountCodes)].filter(Boolean)
    if (unique.length === 0) return null
    unique.sort((a, b) => b.length - a.length)
    if (unique.length === 1) {
      return escapeRegExp(unique[0])
    }
    return unique.map(escapeRegExp).join('|')
  }

  /** Fetches all requested account balances in a single extended-account-balances call per property. */
  async function fetchAccountBalancesBatch(propertyHrId, accountCodes) {
    const regex = buildAccountCodeRegex([...accountCodes])
    if (!regex) {
      return new Map()
    }
    try {
      const token = JSON.parse(localStorage.auth).token
      const response = await fetch(
        `/services/pmp-accounting/api/v1/accounting/extended-account-balances?` +
        `endDate=${"2028-12-31"}&` +
        `propertyHrId=${propertyHrId}&` +
        `regex=${encodeURIComponent(regex)}&` +
        `startDate=${"1990-01-01"}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      )
      const data = await response.json()
      const out = new Map()
      for (const code of accountCodes) {
        const entry = data[code]
        if (entry && entry.normalizedBalanceAtEndOfDateRange !== undefined && entry.normalizedBalanceAtEndOfDateRange !== null) {
          out.set(code, entry.normalizedBalanceAtEndOfDateRange)
        } else {
          out.set(code, null)
        }
      }
      return out
    } catch (error) {
      console.error('Error fetching account balances batch:', error)
      const out = new Map()
      for (const code of accountCodes) {
        out.set(code, null)
      }
      return out
    }
  }

  function appendBalanceToRow(row, amountTd, balance) {
    const showNa = balance === null || balance === undefined
    const formattedNumber = document.createElement('div')
    formattedNumber.className = 'formatted-number'
    formattedNumber.textContent = showNa ? 'N/A' : formatNumber(balance)
    formattedNumber.style.fontSize = '0.8em'
    formattedNumber.style.color = '#666'
    formattedNumber.style.marginTop = '2px'
    if (!row.querySelector('.formatted-number')) {
      amountTd.appendChild(formattedNumber)
    }
  }

  async function processAllRows() {
    const rows = Array.from(document.querySelectorAll('tr[data-row-key^="account-"]')).filter(
      (row) => !row.querySelector('.formatted-number')
    )
    if (rows.length === 0) {
      return
    }

    const jobs = []
    for (const row of rows) {
      const rowKey = row.getAttribute('data-row-key')
      const amountTd = row.querySelector('td:has(.AmountTypography)')
      if (!amountTd) {
        continue
      }
      const accountCode = extractUnitId(rowKey)
      const unitId = rowKey.split('-')[1]
      if (!unitId) {
        continue
      }
      jobs.push({ row, rowKey, accountCode, unitId, amountTd })
    }
    if (jobs.length === 0) {
      return
    }

    try {
      await Promise.all(
        jobs.map(async (job) => {
          job.propertyId = await fetchUnitDetails(job.unitId)
        })
      )
      await Promise.all(
        jobs.map(async (job) => {
          if (job.propertyId) {
            job.propertyHrId = await fetchPropertyHrId(job.propertyId)
          }
        })
      )

      const byPropertyHrId = new Map()
      for (const job of jobs) {
        if (!job.propertyHrId) {
          continue
        }
        if (!byPropertyHrId.has(job.propertyHrId)) {
          byPropertyHrId.set(job.propertyHrId, { accountCodes: new Set(), jobs: [] })
        }
        const g = byPropertyHrId.get(job.propertyHrId)
        g.accountCodes.add(job.accountCode)
        g.jobs.push(job)
      }

      const balanceMaps = new Map(
        await Promise.all(
          [...byPropertyHrId.entries()].map(async ([propertyHrId, { accountCodes }]) => {
            const m = await fetchAccountBalancesBatch(propertyHrId, accountCodes)
            return [propertyHrId, m]
          })
        )
      )

      for (const [propertyHrId, { jobs: groupJobs }] of byPropertyHrId) {
        const map = balanceMaps.get(propertyHrId) || new Map()
        for (const job of groupJobs) {
          if (job.row.querySelector('.formatted-number')) {
            continue
          }
          const balance = map.get(job.accountCode)
          appendBalanceToRow(job.row, job.amountTd, balance)
        }
      }
    } catch (error) {
      console.error('Error processing bank transaction rows:', error)
    }
  }

  const waitForPageLoad = setInterval(() => {
    const pageContent = document.querySelector('.BankTransactionAllocation')
    if (pageContent) {
      clearInterval(waitForPageLoad)

      // Process existing rows immediately
      processAllRows()

      // Set up a MutationObserver to watch for DOM changes
      const observer = new MutationObserver((mutations) => {
        let shouldProcess = false
        mutations.forEach((mutation) => {
          // Check if any added nodes contain our target rows
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // ELEMENT_NODE
              if (node.matches('tr[data-row-key^="account-"]') || 
                  node.querySelector('tr[data-row-key^="account-"]')) {
                shouldProcess = true
              }
            }
          })
        })

        if (shouldProcess) {
          processAllRows()
        }
      })

      // Start observing with more comprehensive configuration
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      })

      // Also set up a periodic check to catch any rows we might have missed
      setInterval(processAllRows, 1000)
    }
  }, 100)
}
